import { useState, useCallback, useMemo } from 'react';
import {
  encryptData,
  evaluatePasswordStrength,
  type PasswordStrength,
} from '../../services/encryptionService';
import { useAppStore } from '../../store/appStore';
import { DATA_VERSION } from '../../services/exportService';
import type { BudgetData } from '../../types';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

type DialogStep = 'options' | 'exporting';

const strengthLabels: Record<PasswordStrength, string> = {
  weak: 'Débil',
  medium: 'Media',
  strong: 'Fuerte',
};

const strengthColors: Record<PasswordStrength, string> = {
  weak: 'bg-red-500',
  medium: 'bg-yellow-500',
  strong: 'bg-green-500',
};

const strengthBars: Record<PasswordStrength, number> = {
  weak: 2,
  medium: 5,
  strong: 8,
};

export function ExportDialog({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: ExportDialogProps) {
  const [step, setStep] = useState<DialogStep>('options');
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const passwordStrength = useMemo(
    () => (password ? evaluatePasswordStrength(password) : null),
    [password]
  );

  const passwordsMatch = password === confirmPassword;
  const canExport =
    !usePassword || (password.length > 0 && passwordsMatch && confirmPassword);

  const resetAndClose = useCallback(() => {
    setStep('options');
    setUsePassword(false);
    setPassword('');
    setConfirmPassword('');
    onClose();
  }, [onClose]);

  const handleExport = useCallback(async () => {
    setStep('exporting');

    try {
      const state = useAppStore.getState();

      const budgetData: BudgetData = {
        version: DATA_VERSION,
        exportedAt: new Date().toISOString(),
        transactions: state.transactions,
        categories: state.categories,
        budgets: state.budgets,
        corrections: state.corrections,
      };

      let blob: Blob;
      let filename: string;

      if (usePassword && password) {
        const jsonData = JSON.stringify(budgetData, null, 2);
        const encryptedPayload = await encryptData(jsonData, password);
        blob = new Blob([JSON.stringify(encryptedPayload, null, 2)], {
          type: 'application/json',
        });
        filename = `budgeter-${new Date().toISOString().slice(0, 10)}-encrypted.json`;
      } else {
        blob = new Blob([JSON.stringify(budgetData, null, 2)], {
          type: 'application/json',
        });
        filename = `budgeter-${new Date().toISOString().slice(0, 10)}.json`;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      onSuccess(
        usePassword
          ? 'Datos exportados con contraseña'
          : 'Datos exportados correctamente'
      );
      resetAndClose();
    } catch {
      onError('Error al exportar los datos');
      setStep('options');
    }
  }, [usePassword, password, onSuccess, onError, resetAndClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={resetAndClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-dialog-title"
      >
        <h2
          id="export-dialog-title"
          className="text-xl font-bold text-near-black mb-4"
        >
          Exportar datos
        </h2>

        {step === 'options' && (
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={usePassword}
                onChange={(e) => setUsePassword(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
              />
              <span className="text-near-black">Proteger con contraseña</span>
            </label>

            {usePassword && (
              <div className="space-y-3 pl-8">
                <div>
                  <label
                    htmlFor="export-password"
                    className="block text-sm text-mid-gray mb-1"
                  >
                    Contraseña:
                  </label>
                  <input
                    id="export-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Introduce una contraseña"
                  />
                </div>

                <div>
                  <label
                    htmlFor="export-confirm-password"
                    className="block text-sm text-mid-gray mb-1"
                  >
                    Confirmar:
                  </label>
                  <input
                    id="export-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-brand-primary ${
                      confirmPassword && !passwordsMatch
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder="Confirma la contraseña"
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-sm text-red-500 mt-1">
                      Las contraseñas no coinciden
                    </p>
                  )}
                </div>

                {passwordStrength && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-mid-gray">Fortaleza:</span>
                      <div className="flex gap-1 flex-1">
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={i}
                            className={`h-2 flex-1 rounded ${
                              i < strengthBars[passwordStrength.strength]
                                ? strengthColors[passwordStrength.strength]
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          passwordStrength.strength === 'weak'
                            ? 'text-red-600'
                            : passwordStrength.strength === 'medium'
                              ? 'text-yellow-600'
                              : 'text-green-600'
                        }`}
                      >
                        {strengthLabels[passwordStrength.strength]}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <span className="text-yellow-600 text-lg">⚠️</span>
                  <p className="text-sm text-yellow-800">
                    Si olvidas tu contraseña, los datos serán irrecuperables
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={resetAndClose}
                className="px-4 py-2 text-mid-gray hover:text-near-black transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleExport}
                disabled={!canExport}
                className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Exportar
              </button>
            </div>
          </div>
        )}

        {step === 'exporting' && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            <span className="ml-3 text-mid-gray">Exportando...</span>
          </div>
        )}
      </div>
    </div>
  );
}
